import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  ArrowLeft, ArrowDown, ArrowUp, Info, Download, BookOpen,
  Wallet, TrendingUp, ExternalLink, CircleDollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  budget_allocation:    { label: 'Budget allocation', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dirIcon: ArrowUp,    dirColor: 'text-emerald-700' },
  budget_category:      { label: 'Category allocation', cls: 'bg-slate-50 text-slate-600 border-slate-200',     dirIcon: Info,       dirColor: 'text-slate-500' },
  expense:              { label: 'Expense',           cls: 'bg-rose-50 text-rose-700 border-rose-200',          dirIcon: ArrowDown,  dirColor: 'text-rose-600' },
  reimbursement_expense:{ label: 'Reimbursement',     cls: 'bg-amber-50 text-amber-700 border-amber-200',       dirIcon: ArrowDown,  dirColor: 'text-amber-700' },
  payment_paid:         { label: 'Payment (paid)',    cls: 'bg-rose-50 text-rose-700 border-rose-200',          dirIcon: ArrowDown,  dirColor: 'text-rose-600' },
  payment_pending:      { label: 'Payment (pending)', cls: 'bg-slate-50 text-slate-600 border-slate-200',       dirIcon: Info,       dirColor: 'text-slate-500' },
  certification:        { label: 'Certification',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200',    dirIcon: Info,       dirColor: 'text-indigo-600' },
};

const fmtMoney = (v) => `€ ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSignedMoney = (v) => {
  const n = Number(v || 0);
  return `${n >= 0 ? '+' : '−'} € ${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB');
};

const Financials = ({ user }) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [tab, setTab] = useState('ledger');
  const [loading, setLoading] = useState(true);

  // Ledger state
  const [movements, setMovements] = useState([]);
  const [totals, setTotals] = useState({ credits: 0, debits: 0, net: 0, by_type: {}, movement_count: 0 });
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Funder state
  const [funders, setFunders] = useState([]);
  const [funderSummary, setFunderSummary] = useState({
    funder_count: 0, total_allocated: 0, total_spent: 0, total_certified: 0, project_count: 0,
  });

  const fetchProjects = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/projects`, { headers });
      setProjects(r.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterProject !== 'all') params.set('project_id', filterProject);
      if (filterType !== 'all') params.set('type', filterType);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      params.set('limit', '1000');
      const r = await axios.get(`${BACKEND_URL}/api/financials/ledger?${params.toString()}`, { headers });
      setMovements(r.data?.movements || []);
      setTotals(r.data?.totals || { credits: 0, debits: 0, net: 0, by_type: {}, movement_count: 0 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load ledger');
    } finally { setLoading(false); }
  };

  const fetchFunders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterProject !== 'all') params.set('project_id', filterProject);
      const r = await axios.get(`${BACKEND_URL}/api/financials/funder-breakdown?${params.toString()}`, { headers });
      setFunders(r.data?.funders || []);
      setFunderSummary(r.data?.summary || funderSummary);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load funder breakdown');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchProjects(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (tab === 'ledger') fetchLedger();
    else fetchFunders();
    /* eslint-disable-next-line */
  }, [tab, filterProject, filterType, fromDate, toDate]);

  const csvLedger = () => {
    if (!movements.length) { toast.info('Nothing to export'); return; }
    const cols = ['date', 'type', 'project_title', 'funder_name', 'category', 'description', 'direction', 'amount', 'running_balance'];
    const header = cols.join(',');
    const rows = movements.map((m) => cols.map((c) => {
      const v = m[c];
      const s = (v ?? '').toString().replace(/"/g, '""');
      return `"${s}"`;
    }).join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Ledger exported');
  };

  // Compute final running balance per project from the movements list
  const finalBalanceByProject = useMemo(() => {
    const out = {};
    for (const m of movements) {
      out[m.project_id] = m.running_balance ?? out[m.project_id] ?? 0;
    }
    return out;
  }, [movements]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="financials-title">
                <CircleDollarSign className="w-7 h-7 text-emerald-700" /> Financials
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Read-only audit ledger of every cash-affecting movement, plus per-funder breakdowns.
              </p>
            </div>
          </div>
          {tab === 'ledger' && (
            <Button onClick={csvLedger} variant="outline" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="ledger" data-testid="tab-ledger"><BookOpen className="w-4 h-4 mr-2" /> Ledger</TabsTrigger>
            <TabsTrigger value="funders" data-testid="tab-funders"><Wallet className="w-4 h-4 mr-2" /> Per-Funder</TabsTrigger>
          </TabsList>

          {/* ─── Ledger ────────────────────────────────────────────── */}
          <TabsContent value="ledger" className="space-y-4">
            {/* Summary tiles */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card data-testid="tile-credits">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Total Credits (in)</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">{fmtMoney(totals.credits)}</p>
                </CardContent>
              </Card>
              <Card data-testid="tile-debits">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Total Debits (out)</p>
                  <p className="text-2xl font-bold text-rose-700 mt-1">{fmtMoney(totals.debits)}</p>
                </CardContent>
              </Card>
              <Card data-testid="tile-net">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Net Balance</p>
                  <p className={`text-2xl font-bold mt-1 ${totals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {fmtSignedMoney(totals.net)}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="tile-count">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Movements</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{totals.movement_count}</p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Project</label>
                    <Select value={filterProject} onValueChange={setFilterProject}>
                      <SelectTrigger data-testid="filter-project"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All projects</SelectItem>
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Movement Type</label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        {Object.entries(TYPE_META).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">From</label>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="filter-from" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">To</label>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="filter-to" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ledger table */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Movement Ledger</CardTitle></CardHeader>
              <CardContent>
                {loading ? (
                  <div className="py-10 text-center text-slate-500">Loading…</div>
                ) : movements.length === 0 ? (
                  <div className="py-12 text-center text-slate-500">No movements match the current filters.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="ledger-table">
                      <thead>
                        <tr className="text-left border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Type</th>
                          <th className="py-2 pr-3">Project</th>
                          <th className="py-2 pr-3">Funder</th>
                          <th className="py-2 pr-3">Category</th>
                          <th className="py-2 pr-3">Description</th>
                          <th className="py-2 pr-3 text-right">Amount</th>
                          <th className="py-2 pr-3 text-right">Running balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map((m) => {
                          const meta = TYPE_META[m.type] || { label: m.type, cls: 'bg-slate-50 text-slate-600 border-slate-200', dirIcon: Info, dirColor: 'text-slate-500' };
                          const Icon = meta.dirIcon;
                          return (
                            <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${m.id}`}>
                              <td className="py-3 pr-3 whitespace-nowrap">{fmtDate(m.date)}</td>
                              <td className="py-3 pr-3">
                                <Badge className={`${meta.cls} border`} data-testid={`type-badge-${m.id}`}>
                                  <Icon className={`w-3 h-3 mr-1 ${meta.dirColor}`} /> {meta.label}
                                </Badge>
                              </td>
                              <td className="py-3 pr-3 max-w-[180px] truncate">
                                <Link to={`/project/${m.project_id}`} className="text-emerald-700 hover:underline">
                                  {m.project_title || m.project_id?.slice(0, 8)}
                                </Link>
                              </td>
                              <td className="py-3 pr-3 max-w-[140px] truncate text-slate-600">{m.funder_name}</td>
                              <td className="py-3 pr-3 text-slate-600">{m.category}</td>
                              <td className="py-3 pr-3 max-w-[260px]">
                                <p className="truncate" title={m.description}>{m.description}</p>
                                {m.receipt_url && (
                                  <a href={m.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-600 mt-0.5">
                                    <ExternalLink className="w-3 h-3 mr-1" /> Receipt
                                  </a>
                                )}
                              </td>
                              <td className={`py-3 pr-3 text-right font-mono ${m.direction === 'in' ? 'text-emerald-700' : m.direction === 'out' ? 'text-rose-700' : 'text-slate-500'}`}>
                                {m.direction === 'info' ? fmtMoney(m.amount) : fmtSignedMoney(m.balance_effect || 0)}
                              </td>
                              <td className="py-3 pr-3 text-right font-mono text-slate-700">
                                {fmtSignedMoney(m.running_balance || 0)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Per-project final balance */}
            {Object.keys(finalBalanceByProject).length > 1 && (
              <Card>
                <CardHeader><CardTitle className="text-lg">Final balance by project</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {projects
                      .filter((p) => finalBalanceByProject[p.id] !== undefined)
                      .map((p) => {
                        const bal = finalBalanceByProject[p.id] || 0;
                        return (
                          <div key={p.id} className="border border-slate-200 rounded p-3 hover:border-emerald-300 transition" data-testid={`final-bal-${p.id}`}>
                            <p className="text-xs text-slate-500 truncate" title={p.title}>{p.title}</p>
                            <p className={`text-lg font-bold mt-1 ${bal >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {fmtSignedMoney(bal)}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ─── Per-Funder ────────────────────────────────────────── */}
          <TabsContent value="funders" className="space-y-4">
            {/* Filter */}
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Project (optional)</label>
                    <Select value={filterProject} onValueChange={setFilterProject}>
                      <SelectTrigger data-testid="funder-filter-project"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All projects</SelectItem>
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card data-testid="funder-tile-count">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Funders</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{funderSummary.funder_count}</p>
                </CardContent>
              </Card>
              <Card data-testid="funder-tile-allocated">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Total Allocated</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">{fmtMoney(funderSummary.total_allocated)}</p>
                </CardContent>
              </Card>
              <Card data-testid="funder-tile-spent">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Total Spent</p>
                  <p className="text-2xl font-bold text-rose-700 mt-1">{fmtMoney(funderSummary.total_spent)}</p>
                </CardContent>
              </Card>
              <Card data-testid="funder-tile-certified">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Certified</p>
                  <p className="text-2xl font-bold text-indigo-700 mt-1">{fmtMoney(funderSummary.total_certified)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Funder cards */}
            {loading ? (
              <Card><CardContent className="py-10 text-center text-slate-500">Loading…</CardContent></Card>
            ) : funders.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">No funder data available.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {funders.map((f) => (
                  <Card key={f.funder_id || 'unattributed'} data-testid={`funder-card-${f.funder_id || 'unattributed'}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-emerald-700" /> {f.funder_name}
                          </CardTitle>
                          <p className="text-xs text-slate-500 mt-1">
                            {f.project_count} project{f.project_count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200 border" data-testid={`util-${f.funder_id || 'unattributed'}`}>
                          <TrendingUp className="w-3 h-3 mr-1" /> {f.utilisation_pct}% used
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                          <p className="text-xs text-emerald-700 uppercase tracking-wide">Allocated</p>
                          <p className="font-mono font-semibold">{fmtMoney(f.allocated_budget)}</p>
                        </div>
                        <div className="bg-rose-50 border border-rose-200 rounded p-2">
                          <p className="text-xs text-rose-700 uppercase tracking-wide">Spent</p>
                          <p className="font-mono font-semibold">{fmtMoney(f.total_expenses)}</p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded p-2">
                          <p className="text-xs text-slate-600 uppercase tracking-wide">Remaining</p>
                          <p className={`font-mono font-semibold ${f.remaining_budget >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {fmtMoney(f.remaining_budget)}
                          </p>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-200 rounded p-2">
                          <p className="text-xs text-indigo-700 uppercase tracking-wide">Certified</p>
                          <p className="font-mono font-semibold">{fmtMoney(f.certified_amount)}</p>
                        </div>
                      </div>

                      {/* Utilisation bar */}
                      <div>
                        <div className="h-2 bg-slate-100 rounded overflow-hidden">
                          <div
                            className={`h-full ${f.utilisation_pct >= 100 ? 'bg-rose-500' : f.utilisation_pct >= 80 ? 'bg-amber-700' : 'bg-emerald-700'}`}
                            style={{ width: `${Math.min(100, f.utilisation_pct)}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                          <span>Reimbursements within: {fmtMoney(f.reimbursement_expenses)}</span>
                          <span>Pending payments: {fmtMoney(f.pending_payments)}</span>
                        </div>
                      </div>

                      {/* Per-project list */}
                      <details className="group">
                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-800 select-none" data-testid={`project-list-${f.funder_id || 'unattributed'}`}>
                          {f.project_count} project{f.project_count === 1 ? '' : 's'} (click to expand)
                        </summary>
                        <div className="mt-2 space-y-1">
                          {f.projects.map((p) => (
                            <Link
                              key={p.id}
                              to={`/project/${p.id}`}
                              className="flex items-center justify-between text-xs border border-slate-100 rounded p-2 hover:bg-slate-50 hover:border-emerald-200"
                            >
                              <div className="truncate flex-1">
                                <p className="font-medium text-slate-800 truncate">{p.title}</p>
                                <p className="text-slate-500">
                                  {fmtMoney(p.spent)} / {fmtMoney(p.budget)} · {p.utilisation_pct}%
                                </p>
                              </div>
                              <ExternalLink className="w-3 h-3 text-slate-500 ml-2" />
                            </Link>
                          ))}
                        </div>
                      </details>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Financials;
